import { AudienceFragment, GeneralSurveyFragment } from '@occc/graphql';

import {
  configureFormInitialValues,
  scheduleFormInitialValues,
  targetFormInitialValues,
} from '../initialValues';
import { GeneralSurveyConfigureFormInitialValues } from '../components/ConfigureForm/types';
import { getIncentivizeResponsesInitialValues } from '../../Wallet/WalletEditPage/utils';
import { GeneralSurveyTargetFormInitialValues } from '../components/TargetForm/types';
import { GeneralSurveyScheduleFormInitialValues } from '../components/ScheduleForm/types';
import { parseDate } from '../../../lib/date';

type ConvertSurveyToInitialValuesParams = {
  survey?: GeneralSurveyFragment;
  audience?: AudienceFragment;
};

type ConvertSurveyToInitialValuesReturns = {
  configure: GeneralSurveyConfigureFormInitialValues;
  target: GeneralSurveyTargetFormInitialValues;
  schedule: GeneralSurveyScheduleFormInitialValues;
};

const defaultInitialValues = {
  configure: configureFormInitialValues,
  target: targetFormInitialValues,
  schedule: scheduleFormInitialValues,
};

export const convertSurveyToInitialValues = ({
  survey,
  audience,
}: ConvertSurveyToInitialValuesParams): ConvertSurveyToInitialValuesReturns => {
  if (!survey || !audience) {
    return defaultInitialValues;
  }
  const question = survey.questions[0];
  if (!question) {
    return defaultInitialValues;
  }

  const configure: GeneralSurveyConfigureFormInitialValues = {
    name: survey.name,
    description: survey.description,
    questionType: question.__typename,
    question: question.questionText,
    incentivizeResponses: getIncentivizeResponsesInitialValues(survey.award),
  };

  if (question.__typename === 'SurveyQuestionIntSlider') {
    configure.rangeMin = question.min;
    configure.rangeMax = question.max;
    configure.options = ['', ''];
  } else {
    configure.options = question.options.map(({ label }) => label);
  }

  const target: GeneralSurveyTargetFormInitialValues = {
    inAudience: {
      id: audience.uuid,
      audience: {
        uuid: audience.uuid,
        membershipCount: audience.membershipCount,
        name: audience.name,
        status: audience?.status,
        hasBeenBackFilled: audience?.hasBeenBackFilled,
        targetingAttributes: audience?.targetingAttributes,
      },
    },
  };

  const publishAt = parseDate(survey.configuration.startDate);
  const expireAt = survey.configuration.endDate
    ? parseDate(survey.configuration.endDate)
    : undefined;

  const schedule: GeneralSurveyScheduleFormInitialValues = {
    publishAtDate: publishAt,
    publishAtTime: publishAt,
    expireAtDate: expireAt || null,
    expireAtTime: expireAt || null,
  };

  return { configure, target, schedule };
};
